import { useEffect, useRef, useState } from 'react';

import type { SimpleSliderRef } from '@capsule/consumer-design-system';
import { mediaQuerySizes, SimpleSlider, Typography, Modal } from '@capsule/consumer-design-system';

import { useStore } from 'react-redux';
import styled, { css } from 'styled-components';

import FixedFloat from 'src/app/components/Elements/Page/Standard/FixedFloat';
import { ToastBanner } from 'src/app/components/Elements/ToastBanner';
import {
  CartItemsCount,
  CartItemsCountContainer,
} from 'src/app/components/Elements/ToastBanner/presets/ViewOrder/styles';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { getCartItems } from 'src/app/state/selectors';
import type { OTCItemV2 } from 'src/app/types';

import { GalleryModal } from '../EssentialsItemImageGalleryModal/GelleryModal';
import { essentialsMoneyFormatter } from '../utils';

import { CartControls } from './CartControls';

interface ContentProps {
  isItemInCart: boolean;
}

const Content = styled.div<ContentProps>`
  display: grid;
  grid-template-columns: 1fr; /* Default to one column */
  gap: ${({ theme }) => theme.space.s6};
  max-width: 855px;
  padding: 24px;
  ${({ theme }) => theme.mediaQueries.small} {
    grid-template-columns: 1fr 1fr; /* Two columns for screens wider than 500px */
    border-bottom: 1px solid var(--Grey-10, #c8ccd0);
  }

  ${({ theme }) => theme.mediaQueries.smallDown} {
    border-top: 1px solid var(--Grey-10, #c8ccd0);
    padding-bottom: ${props => (props.isItemInCart ? '147px' : '0px')};
    height: 100vh;
    display: flex;
    flex-direction: column;
    gap: 0;
  }
`;

export const BagImage = styled.img``;

export const ItemImage = styled.img`
  display: flex;
  object-fit: contain;
  justify-content: center;
  overflow: auto;
  height: 100%;
  max-height: 300px;
  width: 100%;
`;

export const PharmacistNotes = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.capsuleGreen00};
  position: relative;
  border-radius: 16px;
`;

const PharmacistNotesArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="43" height="32" viewBox="0 0 43 32" fill="none">
    <path
      d="M13.447 23.1092C20.4808 15.5046 20.6552 3.59104 21 0L42.5 31.9812L0.65588 31.9812C0.395584 32.0074 0.1764 32.0052 0 31.9812H0.65588C2.68509 31.777 7.21286 29.8492 13.447 23.1092Z"
      fill="#EFF5F5"
    />
  </svg>
);

const PharmacistNotesArrowContainer = styled.div`
  position: absolute;
  left: -21px;
  bottom: -5px;
`;

const Details = styled.div`
  & > div:not(:last-child) {
    margin-bottom: 24px; /* Add bottom margin to all but the last child */
  }
`;

const Section = styled.div`
  header {
    margin-bottom: 12px;
  }
`;

const LargeScreenOnlySection = styled.div`
  ${props => props.theme.mediaQueries.smallDown} {
    display: none;
  }
`;

const SmallScreenOnlySection = styled.div`
  ${props => props.theme.mediaQueries.small} {
    display: none;
  }
`;

const FooterContainer = styled.div`
  padding: 24px;
`;

const StyledFixedFloat = styled(FixedFloat)`
  bottom: 0;
  padding-top: 36px;

  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.4) 8.85%,
    rgba(255, 255, 255, 0.8) 21.35%,
    rgba(255, 255, 255, 0.9) 58.33%,
    #ffffff 100%
  );

  ${props => props.theme.mediaQueries.small} {
    display: none;
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  min-width: 0; /* carousel needs this to calculate width */
  flex-grow: 0;

  ${props => props.theme.mediaQueries.smallDown} {
    padding-bottom: 24px;
  }
`;

const BodyText = styled(Typography)`
  color: ${({ theme }) => theme.colors.capsuleBlue30};
`;

const Badge = styled.div`
  padding: 2px 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.capsuleGreen10};
  height: 24px;
  width: 75px;
  margin-bottom: ${({ theme }) => theme.space.s2};
`;

export interface EssentialsItemDetailsModalProps extends OTCItemV2 {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onAddToCart?: () => void;
  onRemoveFromCart?: () => void;
  position?: number;
}

export const EssentialsItemDetailsModal = ({
  id,
  price,
  name,
  dosage,
  description,
  pharmacistNotes,
  minQuantity,
  maxQuantity,
  isOpen,
  activeIngredients,
  imageURLs,
  setIsOpen,
  position,
  badge,
}: EssentialsItemDetailsModalProps) => {
  const trackAddToCartEvent = useSegmentEvent('addToCart', { shouldTrackOnMount: false });
  const trackRemoveFromCartEvent = useSegmentEvent('removeFromCart', { shouldTrackOnMount: false });
  const trackViewEssentialsImageEvent = useSegmentEvent('viewEssentialsImage', { shouldTrackOnMount: false });

  const store = useStore();
  const state = store.getState();

  const sliderRef = useRef<SimpleSliderRef>(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const otcCartItem = getCartItems(state).filter(item => item.otcItemId === id)[0];
  let quantity = otcCartItem?.quantity;
  const minQuantityWithDefault = minQuantity ?? 1;
  if (!quantity || quantity <= 0) quantity = minQuantityWithDefault;

  const analyticsCartManipulationPayload = {
    display_name_primary: name,
    dosage: dosage,
    price: essentialsMoneyFormatter.format(price),
    quantity: quantity ?? 1,
    OTC: true,
    patientmedication_id: null,
    prescription_fulfillment_id: null,
    page: 'essentials_product_listing',
  };

  const isHsaFsaEligible = badge?.name === 'HSA/FSA';

  const [isItemInCart, setIsItemInCart] = useState(otcCartItem?.quantity !== undefined && otcCartItem.quantity > 0);

  useEffect(() => {
    setIsItemInCart(otcCartItem?.quantity !== undefined && otcCartItem.quantity > 0);
  }, [otcCartItem]);

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.update();
    }
  }, []);

  return (
    <Modal
      isBodyPaddingDisabled={true}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      children={
        <>
          <GalleryModal isOpen={isGalleryOpen} setIsOpen={setIsGalleryOpen} imageURLs={imageURLs} />
          <Content isItemInCart={isItemInCart}>
            <CarouselContainer onClick={e => e.stopPropagation()}>
              {imageURLs.length > 0 && (
                <SimpleSlider
                  ref={sliderRef}
                  slidesPerView={1}
                  slidesPerGroup={1}
                  isLooped={true}
                  isNavigationButtonVisible={true}
                  isPaginationCircleVisible={true}
                  slides={imageURLs.map((url, index) => (
                    <div
                      css={css`
                        display: flex;
                        align-content: center;
                        justify-content: center;
                        height: 100%;
                      `}
                    >
                      <ItemImage
                        src={url}
                        key={index}
                        alt=""
                        onClick={() => {
                          trackViewEssentialsImageEvent({ display_name_primary: name });
                          setIsGalleryOpen(true);
                        }}
                      />
                    </div>
                  ))}
                  breakpoints={{
                    1: {
                      slidesPerView: 1,
                      slidesPerGroup: 1,
                      isLooped: true,
                      isNavigationButtonVisible: false,
                      isPaginationCircleVisible: true,
                    },
                    [mediaQuerySizes.small]: {
                      slidesPerView: 1,
                      slidesPerGroup: 1,
                      isLooped: true,
                      isNavigationButtonVisible: true,
                      isPaginationCircleVisible: true,
                    },
                  }}
                />
              )}
            </CarouselContainer>
            <Details>
              <Section>
                {isHsaFsaEligible && (
                  <Badge>
                    <Typography variant="small">HSA/FSA</Typography>
                  </Badge>
                )}
                <Typography as="header" variant="heading2">
                  {name}
                </Typography>
                <Typography variant="small">{dosage}</Typography>

                <SmallScreenOnlySection
                  css={css`
                    margin-top: 16px;
                  `}
                >
                  <CartControls
                    id={id}
                    price={price}
                    name={name}
                    minQuantity={minQuantity}
                    maxQuantity={maxQuantity}
                    onAddToCart={() =>
                      trackAddToCartEvent({ ...analyticsCartManipulationPayload, position: `${position}` })
                    }
                    onRemoveFromCart={() => trackRemoveFromCartEvent(analyticsCartManipulationPayload)}
                  />
                </SmallScreenOnlySection>
              </Section>

              <Section>
                <Typography as="header" variant="heading3">
                  Description
                </Typography>
                <BodyText variant="body">{description}</BodyText>
              </Section>

              {Boolean(pharmacistNotes) && (
                <Section>
                  <PharmacistNotes>
                    <Typography as="header" variant="heading4">
                      Pharmacist's note
                    </Typography>
                    <BodyText variant="body">{pharmacistNotes}</BodyText>
                    <PharmacistNotesArrowContainer>
                      <PharmacistNotesArrow />
                    </PharmacistNotesArrowContainer>
                  </PharmacistNotes>
                </Section>
              )}

              {Boolean(activeIngredients) && (
                <Section>
                  <Typography as="header" variant="heading3">
                    Active Ingredients
                  </Typography>
                  <BodyText variant="body">{activeIngredients}</BodyText>
                </Section>
              )}
            </Details>
          </Content>

          <LargeScreenOnlySection>
            <FooterContainer>
              <CartControls
                id={id}
                price={price}
                name={name}
                minQuantity={minQuantity}
                maxQuantity={maxQuantity}
                onAddToCart={() =>
                  trackAddToCartEvent({ ...analyticsCartManipulationPayload, position: `${position}` })
                }
                onRemoveFromCart={() => trackRemoveFromCartEvent(analyticsCartManipulationPayload)}
              />
            </FooterContainer>
          </LargeScreenOnlySection>

          {isItemInCart && (
            <SmallScreenOnlySection>
              <StyledFixedFloat>
                <ToastBanner
                  text={'Continue Shopping'}
                  textVariant={'calloutSmall'}
                  variant={'outlined'}
                  medicationsHomepageHack={false}
                  onClick={() => setIsOpen(false)}
                >
                  <CartItemsCountContainer>
                    <BagImage src="/public/Bag.svg" alt="" />
                    <CartItemsCount>{quantity}</CartItemsCount>
                  </CartItemsCountContainer>
                </ToastBanner>
              </StyledFixedFloat>
            </SmallScreenOnlySection>
          )}
        </>
      }
    />
  );
};
